/**
 * AudioEngine - Real-Time Voice Echo Studio Audio Processor
 * 
 * Manages the Web Audio API graph, microphone stream connections,
 * delay/echo feedback loops, EQ, compression, limiting, and recording.
 */
class AudioEngine {
  constructor() {
    this.audioContext = null;
    this.stream = null;
    this.sourceNode = null;

    // Graph Nodes
    this.inputGainNode = null;
    this.gateNode = null;
    this.gateThreshold = -100;
    this.gateCurrentGain = 1.0;
    this.highpassNode = null;
    this.lowpassNode = null;
    this.eqLowNode = null;
    this.eqMidNode = null;
    this.eqHighNode = null;
    this.noiseReductionHighpassNode = null;
    this.noiseReductionLowpassNode = null;
    this.micFeedbackNotchNode = null;
    this.micHighDampNode = null;
    this.compressorNode = null;
    
    // Delay & Echo Nodes
    this.delayNode = null;
    this.feedbackGainNode = null;
    this.dryGainNode = null;
    this.wetGainNode = null;
    
    // Panning & Reverb
    this.pannerNode = null;
    this.reverbNode = null;
    this.reverbGainNode = null;
    
    // Limiter & Output
    this.limiterNode = null;
    this.masterGainNode = null;
    
    // Analysers
    this.inputAnalyserNode = null;
    this.outputAnalyserNode = null;

    // Recording State
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.isRecording = false;

    // Music Backing Track State
    this.audioElement = null;
    this.musicSourceNode = null;
    this.musicGainNode = null;
    this.musicUrl = null;

    // Active Parameters
    this.params = {
      isMuted: false,
      delayTime: 250, // ms
      feedback: 0.45,
      echoVolume: 0.5, // 0 - 1
      dryWetMix: 50, // 0 - 100
      masterVolume: 0.8, // 0 - 1
      musicVolume: 0.5, // 0 - 1 (backing track volume)
      stereoPan: 0, // -1 to 1
      noiseReduction: true,
      compressor: false,
      reverb: false,
      reverbVolume: 0.3, // 0 - 1
      limiter: true,
      eqLow: 0, // dB
      eqMid: 0, // dB
      eqHigh: 0, // dB
    };
  }

  /**
   * Check browser support for Web Audio API and mediaDevices
   * @returns {boolean}
   */
  static isSupported() {
    return !!(
      window.AudioContext ||
      window.webkitAudioContext ||
      (navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    );
  }

  /**
   * Initialize the AudioContext
   */
  initialize() {
    if (this.audioContext) return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioContextClass({
      latencyHint: "interactive",
    });
  }

  /**
   * Start the audio engine with a microphone stream
   * @param {MediaStream} stream 
   */
  async start(stream) {
    this.initialize();
    this.stream = stream;

    // Resume context if suspended (browser security)
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    const ctx = this.audioContext;

    // 1. Create Source Node
    this.sourceNode = ctx.createMediaStreamSource(stream);

    // 2. Create Analysers
    this.inputAnalyserNode = ctx.createAnalyser();
    this.inputAnalyserNode.fftSize = 512;
    this.outputAnalyserNode = ctx.createAnalyser();
    this.outputAnalyserNode.fftSize = 512;

    // 3. Create General Input & Control Nodes
    this.inputGainNode = ctx.createGain();
    this.inputGainNode.gain.value = this.params.isMuted ? 0 : 1;

    // 4. Create Filter & EQ Nodes
    this.highpassNode = ctx.createBiquadFilter();
    this.highpassNode.type = "highpass";
    this.highpassNode.frequency.value = 20; // Default off (below hearing range)

    this.lowpassNode = ctx.createBiquadFilter();
    this.lowpassNode.type = "lowpass";
    this.lowpassNode.frequency.value = 20000; // Default off (above hearing range)

    // EQ Nodes (Low, Mid, High in series)
    this.eqLowNode = ctx.createBiquadFilter();
    this.eqLowNode.type = "lowshelf";
    this.eqLowNode.frequency.value = 250; // Bass frequency boundary
    this.eqLowNode.gain.value = this.params.eqLow;

    this.eqMidNode = ctx.createBiquadFilter();
    this.eqMidNode.type = "peaking";
    this.eqMidNode.Q.value = 1.0; // Q bandwidth
    this.eqMidNode.frequency.value = 1500; // Mid-frequency band
    this.eqMidNode.gain.value = this.params.eqMid;

    this.eqHighNode = ctx.createBiquadFilter();
    this.eqHighNode.type = "highshelf";
    this.eqHighNode.frequency.value = 4000; // Treble frequency boundary
    this.eqHighNode.gain.value = this.params.eqHigh;

    // Noise Reduction Filters (Highpass/Lowpass combo when enabled)
    this.noiseReductionHighpassNode = ctx.createBiquadFilter();
    this.noiseReductionHighpassNode.type = "highpass";
    // Cut sub-rumble below 50Hz without touching vocal fundamentals
    this.noiseReductionHighpassNode.frequency.value = this.params.noiseReduction ? 50 : 10;

    this.noiseReductionLowpassNode = ctx.createBiquadFilter();
    this.noiseReductionLowpassNode.type = "lowpass";
    // Preserve vocal harmonics up to 20000Hz (no cutting off sibilants or phonemes)
    this.noiseReductionLowpassNode.frequency.value = this.params.noiseReduction ? 20000 : 22000;

    // Anti-Feedback Notch Filter (cuts 3150Hz speaker feedback resonance squeal/beeping)
    this.micFeedbackNotchNode = ctx.createBiquadFilter();
    this.micFeedbackNotchNode.type = "notch";
    this.micFeedbackNotchNode.frequency.value = 3150;
    this.micFeedbackNotchNode.Q.value = 3.0;

    // High-Frequency Dampening Filter (-3dB at 6.5kHz to stop high-pitched speaker feedback loops)
    this.micHighDampNode = ctx.createBiquadFilter();
    this.micHighDampNode.type = "highshelf";
    this.micHighDampNode.frequency.value = 6500;
    this.micHighDampNode.gain.value = -3;

    // 5. Create Voice Compressor Node
    this.compressorNode = ctx.createDynamicsCompressor();
    // Smooth studio compression settings (preserves word tails & natural voice decay)
    this.compressorNode.threshold.value = -12; // dB
    this.compressorNode.knee.value = 20; // dB
    this.compressorNode.ratio.value = this.params.compressor ? 2.5 : 1.0; // Bypass compression if off
    this.compressorNode.attack.value = 0.015; // 15ms
    this.compressorNode.release.value = 0.25; // 250ms smooth release (no clipping word ends)

    // 6. Create Delay/Echo Nodes
    this.delayNode = ctx.createDelay(2.0); // max delay of 2.0s
    this.delayNode.delayTime.value = this.params.delayTime / 1000; // convert to seconds

    this.feedbackGainNode = ctx.createGain();
    this.feedbackGainNode.gain.value = this.params.feedback;

    // 7. Create Mix & Routing Nodes
    this.dryGainNode = ctx.createGain();
    this.wetGainNode = ctx.createGain();
    this.updateMixGains();

    // 8. Create Panner & Reverb Nodes
    this.pannerNode = ctx.createStereoPanner();
    this.pannerNode.pan.value = this.params.stereoPan;

    this.reverbNode = this.createReverbNode(ctx);
    this.reverbGainNode = ctx.createGain();
    this.reverbGainNode.gain.value = this.params.reverb ? this.params.reverbVolume : 0;

    // 9. Create Limiter Node (to prevent clipping)
    this.limiterNode = ctx.createDynamicsCompressor();
    this.limiterNode.threshold.value = -0.5; // limit right below clipping
    this.limiterNode.knee.value = 0; // hard knee
    this.limiterNode.ratio.value = 20.0; // high ratio for limiter
    this.limiterNode.attack.value = 0.003; // 3ms attack
    this.limiterNode.release.value = 0.08; // 80ms release

    // 10. Create Master Gain
    this.masterGainNode = ctx.createGain();
    this.masterGainNode.gain.value = this.params.masterVolume;

    // ==========================================
    // CONNECTIONS (THE AUDIO GRAPH)
    // ==========================================

    // Source -> Input Analyser -> Input Mute Gain
    this.sourceNode.connect(this.inputAnalyserNode);
    this.inputAnalyserNode.connect(this.inputGainNode);

    // Direct sample-accurate Hardware-accelerated processing chain:
    // Chain: InputGain -> Feedback Notch -> High Damp -> Noise HP -> Noise LP -> EQ Low -> EQ Mid -> EQ High -> Compressor -> Main Signal Split
    this.inputGainNode.connect(this.micFeedbackNotchNode);
    this.micFeedbackNotchNode.connect(this.micHighDampNode);
    this.micHighDampNode.connect(this.noiseReductionHighpassNode);
    this.noiseReductionHighpassNode.connect(this.noiseReductionLowpassNode);
    this.noiseReductionLowpassNode.connect(this.eqLowNode);
    this.eqLowNode.connect(this.eqMidNode);
    this.eqMidNode.connect(this.eqHighNode);
    this.eqHighNode.connect(this.compressorNode);

    // Main Signal Split
    // 1. Dry Path
    this.compressorNode.connect(this.dryGainNode);
    this.dryGainNode.connect(this.pannerNode);

    // 2. Wet (Delay) Path
    this.compressorNode.connect(this.delayNode);
    this.delayNode.connect(this.wetGainNode);
    this.wetGainNode.connect(this.pannerNode);

    // Delay Feedback Loop: DelayNode -> FeedbackGainNode -> DelayNode
    this.delayNode.connect(this.feedbackGainNode);
    this.feedbackGainNode.connect(this.delayNode);

    // 3. Reverb Path (Parallel)
    // Send Compressor (processed dry) output to Reverb
    this.compressorNode.connect(this.reverbNode);
    this.reverbNode.connect(this.reverbGainNode);
    this.reverbGainNode.connect(this.pannerNode);

    // Panner -> Limiter -> Master Gain -> Output Analyser -> Destination (Speaker)
    this.pannerNode.connect(this.limiterNode);

    // Connect music channel if active
    if (this.musicGainNode) {
      try {
        this.musicGainNode.disconnect();
      } catch (e) {}
      this.musicGainNode.connect(this.limiterNode);
    }

    this.limiterNode.connect(this.masterGainNode);
    this.masterGainNode.connect(this.outputAnalyserNode);
    this.outputAnalyserNode.connect(ctx.destination);
  }

  /**
   * Helper to create a synthesized convolution reverb buffer
   */
  createReverbNode(ctx) {
    const rate = ctx.sampleRate;
    const decay = 2.5; // Reverb duration in seconds
    const len = rate * decay;
    const impulse = ctx.createBuffer(2, len, rate);
    const left = impulse.getChannelData(0);
    const right = impulse.getChannelData(1);

    for (let i = 0; i < len; i++) {
      const pct = i / len;
      // Exponential decay envelope
      const envelope = Math.exp(-pct * 4) * (1 - pct);
      // Random white noise
      left[i] = (Math.random() * 2 - 1) * envelope;
      right[i] = (Math.random() * 2 - 1) * envelope;
    }

    const convolver = ctx.createConvolver();
    convolver.buffer = impulse;
    return convolver;
  }

  /**
   * Stops the Audio Engine and clean up active streams/nodes
   */
  stop() {
    this.stopRecording();

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    // Disconnect all nodes if instantiated
    const nodes = [
      this.inputGainNode,
      this.gateNode,
      this.highpassNode,
      this.lowpassNode,
      this.eqLowNode,
      this.eqMidNode,
      this.eqHighNode,
      this.noiseReductionHighpassNode,
      this.noiseReductionLowpassNode,
      this.compressorNode,
      this.delayNode,
      this.feedbackGainNode,
      this.dryGainNode,
      this.wetGainNode,
      this.pannerNode,
      this.reverbGainNode,
      this.limiterNode,
      this.masterGainNode,
      this.inputAnalyserNode,
      this.outputAnalyserNode,
    ];

    nodes.forEach((node) => {
      if (node) {
        try {
          node.disconnect();
        } catch (e) {
          // already disconnected
        }
      }
    });

    if (this.gateNode) {
      this.gateNode.onaudioprocess = null;
      this.gateNode = null;
    }

    if (this.audioElement) {
      this.audioElement.pause();
    }

    if (this.musicGainNode) {
      try {
        this.musicGainNode.disconnect();
      } catch (e) {}
      this.musicGainNode = null;
    }

    if (this.musicSourceNode) {
      try {
        this.musicSourceNode.disconnect();
      } catch (e) {}
      this.musicSourceNode = null;
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  /**
   * Destroy the audio engine
   */
  destroy() {
    this.stop();
  }

  /**
   * Set mute state
   * @param {boolean} isMuted 
   */
  setMute(isMuted) {
    this.params.isMuted = isMuted;
    if (this.inputGainNode && this.audioContext) {
      const targetGain = isMuted ? 0 : 1;
      this.inputGainNode.gain.setTargetAtTime(targetGain, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Update dry and wet gains based on dryWetMix and echoVolume
   */
  updateMixGains() {
    if (!this.audioContext) return;

    const mix = this.params.dryWetMix / 100; // 0 to 1
    const echoVol = this.params.echoVolume; // 0 to 1

    // Constant power crossfade to maintain visual and perceptual volume level
    const dryGain = Math.cos(mix * Math.PI / 2);
    const wetGain = Math.sin(mix * Math.PI / 2) * echoVol;

    if (this.dryGainNode) {
      this.dryGainNode.gain.setTargetAtTime(dryGain, this.audioContext.currentTime, 0.01);
    }
    if (this.wetGainNode) {
      this.wetGainNode.gain.setTargetAtTime(wetGain, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Set delay time in ms
   * @param {number} ms 
   */
  setDelayTime(ms) {
    this.params.delayTime = ms;
    if (this.delayNode && this.audioContext) {
      const delaySec = ms / 1000;
      this.delayNode.delayTime.setTargetAtTime(delaySec, this.audioContext.currentTime, 0.02);
    }
  }

  /**
   * Set delay feedback (0 to 0.95)
   * @param {number} value 
   */
  setFeedback(value) {
    this.params.feedback = value;
    if (this.feedbackGainNode && this.audioContext) {
      this.feedbackGainNode.gain.setTargetAtTime(value, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Set echo volume (0 to 1)
   * @param {number} value 
   */
  setEchoVolume(value) {
    this.params.echoVolume = value;
    this.updateMixGains();
  }

  /**
   * Set dry/wet mix (0 to 100)
   * @param {number} value 
   */
  setDryWetMix(value) {
    this.params.dryWetMix = value;
    this.updateMixGains();
  }

  /**
   * Set master volume (0 to 1)
   * @param {number} value 
   */
  setMasterGain(value) {
    this.params.masterVolume = value;
    if (this.masterGainNode && this.audioContext) {
      this.masterGainNode.gain.setTargetAtTime(value, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Set stereo panning (-1 to 1)
   * @param {number} value 
   */
  setStereoPan(value) {
    this.params.stereoPan = value;
    if (this.pannerNode && this.audioContext) {
      this.pannerNode.pan.setTargetAtTime(value, this.audioContext.currentTime, 0.02);
    }
  }

  /**
   * Set Equalizer low frequency shelf gain (dB)
   * @param {number} db 
   */
  setEqLow(db) {
    this.params.eqLow = db;
    if (this.eqLowNode && this.audioContext) {
      this.eqLowNode.gain.setTargetAtTime(db, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Set Equalizer mid frequency peaking gain (dB)
   * @param {number} db 
   */
  setEqMid(db) {
    this.params.eqMid = db;
    if (this.eqMidNode && this.audioContext) {
      this.eqMidNode.gain.setTargetAtTime(db, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Set Equalizer high frequency shelf gain (dB)
   * @param {number} db 
   */
  setEqHigh(db) {
    this.params.eqHigh = db;
    if (this.eqHighNode && this.audioContext) {
      this.eqHighNode.gain.setTargetAtTime(db, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Set Noise Reduction state
   * @param {boolean} enabled 
   */
  setNoiseReduction(enabled) {
    this.params.noiseReduction = enabled;
    if (this.noiseReductionHighpassNode && this.noiseReductionLowpassNode && this.audioContext) {
      // Noise reduction filters cut rumble below 80Hz while preserving vocal harmonics up to 15000Hz
      const hpFreq = enabled ? 80 : 10;
      const lpFreq = enabled ? 15000 : 22000;
      this.noiseReductionHighpassNode.frequency.setTargetAtTime(hpFreq, this.audioContext.currentTime, 0.05);
      this.noiseReductionLowpassNode.frequency.setTargetAtTime(lpFreq, this.audioContext.currentTime, 0.05);
    }
  }

  /**
   * Set Vocal Compressor state
   * @param {boolean} enabled 
   */
  setCompressor(enabled) {
    this.params.compressor = enabled;
    if (this.compressorNode && this.audioContext) {
      const ratio = enabled ? 3.5 : 1.0;
      this.compressorNode.ratio.setTargetAtTime(ratio, this.audioContext.currentTime, 0.02);
    }
  }

  /**
   * Set Reverb state
   * @param {boolean} enabled 
   */
  setReverb(enabled) {
    this.params.reverb = enabled;
    this.updateReverbVolume();
  }

  /**
   * Set Reverb volume (0 to 1)
   * @param {number} value 
   */
  setReverbVolume(value) {
    this.params.reverbVolume = value;
    this.updateReverbVolume();
  }

  /**
   * Updates reverb node volume based on active parameter status
   */
  updateReverbVolume() {
    if (this.reverbGainNode && this.audioContext) {
      const targetGain = this.params.reverb ? this.params.reverbVolume : 0;
      this.reverbGainNode.gain.setTargetAtTime(targetGain, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Apply all parameters of a Preset
   * @param {object} presetParams 
   */
  applyPreset(presetParams) {
    // Map preset fields to internal parameter setters
    if (presetParams.isMuted !== undefined) this.setMute(presetParams.isMuted);
    if (presetParams.delayTime !== undefined) this.setDelayTime(presetParams.delayTime);
    if (presetParams.feedback !== undefined) this.setFeedback(presetParams.feedback);
    if (presetParams.echoVolume !== undefined) this.setEchoVolume(presetParams.echoVolume);
    if (presetParams.dryWetMix !== undefined) this.setDryWetMix(presetParams.dryWetMix);
    if (presetParams.masterVolume !== undefined) this.setMasterGain(presetParams.masterVolume);
    if (presetParams.stereoPan !== undefined) this.setStereoPan(presetParams.stereoPan);
    if (presetParams.noiseReduction !== undefined) this.setNoiseReduction(presetParams.noiseReduction);
    if (presetParams.compressor !== undefined) this.setCompressor(presetParams.compressor);
    if (presetParams.reverb !== undefined) this.setReverb(presetParams.reverb);
    if (presetParams.reverbVolume !== undefined) this.setReverbVolume(presetParams.reverbVolume);
    if (presetParams.eqLow !== undefined) this.setEqLow(presetParams.eqLow);
    if (presetParams.eqMid !== undefined) this.setEqMid(presetParams.eqMid);
    if (presetParams.eqHigh !== undefined) this.setEqHigh(presetParams.eqHigh);
  }

  /**
   * Get dynamic analyser node data for visualizations
   * @returns {object|null} { inputWaveform, outputFrequency }
   */
  getAnalyserData() {
    if (!this.audioContext || !this.inputAnalyserNode || !this.outputAnalyserNode) {
      return null;
    }

    const inputLength = this.inputAnalyserNode.frequencyBinCount;
    const outputLength = this.outputAnalyserNode.frequencyBinCount;

    const inputWaveform = new Uint8Array(inputLength);
    const outputFrequency = new Uint8Array(outputLength);

    this.inputAnalyserNode.getByteTimeDomainData(inputWaveform);
    this.outputAnalyserNode.getByteFrequencyData(outputFrequency);

    return {
      inputWaveform,
      outputFrequency,
    };
  }

  /**
   * Get instant levels (rms volume) for input and output meters
   * @returns {object} { inputLevel, outputLevel }
   */
  getInstantLevels() {
    if (!this.audioContext || !this.inputAnalyserNode || !this.outputAnalyserNode) {
      return { inputLevel: 0, outputLevel: 0 };
    }

    const inputData = new Uint8Array(this.inputAnalyserNode.fftSize);
    this.inputAnalyserNode.getByteTimeDomainData(inputData);
    let inputRms = 0;
    for (let i = 0; i < inputData.length; i++) {
      const val = (inputData[i] - 128) / 128;
      inputRms += val * val;
    }
    inputRms = Math.sqrt(inputRms / inputData.length);

    const outputData = new Uint8Array(this.outputAnalyserNode.fftSize);
    this.outputAnalyserNode.getByteTimeDomainData(outputData);
    let outputRms = 0;
    for (let i = 0; i < outputData.length; i++) {
      const val = (outputData[i] - 128) / 128;
      outputRms += val * val;
    }
    outputRms = Math.sqrt(outputRms / outputData.length);

    // Convert RMS to volume percentage (0 - 100) with a sensitivity curve
    const inputPct = Math.min(100, Math.round(inputRms * 350));
    const outputPct = Math.min(100, Math.round(outputRms * 350));

    return {
      inputLevel: inputPct,
      outputLevel: outputPct,
    };
  }

  /**
   * Start recording the processed output audio
   */
  startRecording() {
    if (this.isRecording || !this.audioContext || !this.masterGainNode) return false;

    try {
      this.recordedChunks = [];
      const dest = this.audioContext.createMediaStreamDestination();
      this.masterGainNode.connect(dest);

      // We can also record dry microphone if needed, but the requirements ask for processed audio recording
      this.mediaRecorder = new MediaRecorder(dest.stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(100); // chunk every 100ms
      this.isRecording = true;
      return true;
    } catch (e) {
      console.error("Failed to start MediaRecorder:", e);
      return false;
    }
  }

  /**
   * Stop recording, decode WebM buffer, encode to WAV, and return result
   * @returns {Promise<{blob: Blob, url: string, format: string}|null>}
   */
  stopRecording() {
    if (!this.isRecording || !this.mediaRecorder) return Promise.resolve(null);

    return new Promise((resolve) => {
      this.mediaRecorder.onstop = async () => {
        const webmBlob = new Blob(this.recordedChunks, { type: "audio/webm" });
        this.isRecording = false;
        
        try {
          // 1. Decode WebM to raw PCM AudioBuffer
          const arrayBuffer = await webmBlob.arrayBuffer();
          const decodedBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
          
          // 2. Encode PCM to standard 16-bit Stereo WAV format
          const wavBlob = this.audioBufferToWav(decodedBuffer);
          const url = URL.createObjectURL(wavBlob);
          
          resolve({ blob: wavBlob, url, format: "wav" });
        } catch (error) {
          console.warn("WAV rendering failed, falling back to raw WebM recording:", error);
          const url = URL.createObjectURL(webmBlob);
          resolve({ blob: webmBlob, url, format: "webm" });
        }
      };

      if (this.mediaRecorder.state !== "inactive") {
        try {
          this.mediaRecorder.requestData();
        } catch (e) {}
      }
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    });
  }

  /**
   * Encodes an AudioBuffer into 16-bit Stereo WAV file format
   */
  audioBufferToWav(buffer) {
    const numOfChan = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // 1 = raw LPCM
    const bitDepth = 16;
    
    let result;
    if (numOfChan === 2) {
      result = this.interleave(buffer.getChannelData(0), buffer.getChannelData(1));
    } else {
      // If mono, convert to stereo by duplicating channel
      const monoData = buffer.getChannelData(0);
      result = this.interleave(monoData, monoData);
    }
    
    const bufferLength = result.length * 2; // 16-bit = 2 bytes per sample
    const wavBuffer = new ArrayBuffer(44 + bufferLength);
    const view = new DataView(wavBuffer);
    
    // RIFF header
    this.writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + bufferLength, true);
    this.writeString(view, 8, "WAVE");
    
    // fmt chunk
    this.writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true); // chunk length
    view.setUint16(20, format, true); // raw PCM
    view.setUint16(22, 2, true); // force 2 channels (stereo)
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2 * (bitDepth / 8), true); // byte rate
    view.setUint16(32, 2 * (bitDepth / 8), true); // block align
    view.setUint16(34, bitDepth, true); // bits per sample
    
    // data chunk
    this.writeString(view, 36, "data");
    view.setUint32(40, bufferLength, true);
    
    // Write audio samples
    this.floatTo16BitPCM(view, 44, result);
    
    return new Blob([view], { type: "audio/wav" });
  }

  interleave(inputL, inputR) {
    const length = inputL.length + inputR.length;
    const result = new Float32Array(length);
    let index = 0;
    let inputIndex = 0;
    
    while (index < length) {
      result[index++] = inputL[inputIndex];
      result[index++] = inputR[inputIndex];
      inputIndex++;
    }
    return result;
  }

  floatTo16BitPCM(output, offset, input) {
    for (let i = 0; i < input.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  }

  writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  /**
   * Loads a backing track file into the Audio Element
   */
  loadMusic(file, onStateChange) {
    this.initialize();

    if (!this.audioElement) {
      this.audioElement = new Audio();
      this.audioElement.loop = true;

      this.musicSourceNode = this.audioContext.createMediaElementSource(this.audioElement);
      this.musicGainNode = this.audioContext.createGain();
      this.musicGainNode.gain.value = this.params.musicVolume;

      this.musicSourceNode.connect(this.musicGainNode);

      if (this.limiterNode) {
        this.musicGainNode.connect(this.limiterNode);
      } else {
        this.musicGainNode.connect(this.audioContext.destination);
      }
    }

    if (this.musicUrl) {
      URL.revokeObjectURL(this.musicUrl);
    }

    this.musicUrl = URL.createObjectURL(file);
    this.audioElement.src = this.musicUrl;
    this.audioElement.load();

    this.audioElement.onplay = () => onStateChange({ playing: true });
    this.audioElement.onpause = () => onStateChange({ playing: false });
    this.audioElement.onended = () => onStateChange({ playing: false });
    this.audioElement.ontimeupdate = () => {
      onStateChange({
        currentTime: this.audioElement.currentTime,
        duration: this.audioElement.duration || 0,
      });
    };
  }

  playMusic() {
    this.initialize();
    if (this.audioContext.state === "suspended") {
      this.audioContext.resume();
    }

    // Re-create node mapping if AudioContext was restarted
    if (!this.musicSourceNode && this.audioElement) {
      this.musicSourceNode = this.audioContext.createMediaElementSource(this.audioElement);
      this.musicGainNode = this.audioContext.createGain();
      this.musicGainNode.gain.value = this.params.musicVolume;
      this.musicSourceNode.connect(this.musicGainNode);

      if (this.limiterNode) {
        this.musicGainNode.connect(this.limiterNode);
      } else {
        this.musicGainNode.connect(this.audioContext.destination);
      }
    }

    if (this.audioElement) {
      this.audioElement.play().catch((e) => console.error("Music play failed:", e));
    }
  }

  pauseMusic() {
    if (this.audioElement) {
      this.audioElement.pause();
    }
  }

  seekMusic(time) {
    if (this.audioElement) {
      this.audioElement.currentTime = time;
    }
  }

  setMusicVolume(value) {
    this.params.musicVolume = value;
    if (this.musicGainNode && this.audioContext) {
      this.musicGainNode.gain.setTargetAtTime(value, this.audioContext.currentTime, 0.01);
    }
  }
}

export default AudioEngine;
